/* eslint no-undef: "off" */

import { createI18n } from "vue-i18n";
import { enhanceI18nWithRemote } from "@emcd-shared/translate-service";

// Locales are flat, generic JSON — one file per language in src/shared/locales.
// Keys are pipe-delimited (product|feature|element); they contain no dots, so
// vue-i18n resolves each as a literal top-level key. The committed JSON is the
// local floor; remote translations (translate.emcd.io) are merged on top at
// runtime by enhanceI18nWithRemote and refreshed periodically. Managed via Crowdin.
function loadLocaleMessages() {
  const modules = import.meta.glob("@/shared/locales/*.json", { eager: true });
  const messages = {};
  for (const path in modules) {
    const locale = path.match(/([\w-]+)\.json$/)[1];
    if (locale === "actual-date") continue; // generated meta file, not a locale
    messages[locale] = modules[path].default;
  }
  return messages;
}

// Crowdin plurals arrive as CLDR objects ({ one, few, many, other }); turn them
// into the pipe layout pluralRules.ru expects: 4 forms with `few`, 3 without.
function pluralObjectToPipe(value) {
  if (!value || typeof value !== "object" || !("other" in value)) return value;
  const many = value.many ?? value.other;
  if (value.few) {
    return [value.zero ?? many, value.one ?? many, value.few, many].join(" | ");
  }
  return [
    value.zero ?? value.other,
    value.one ?? value.other,
    value.other,
  ].join(" | ");
}

const instance = createI18n({
  legacy: false,
  locale: "en",
  warnHtmlInMessage: "off",
  fallbackLocale: "en",
  messages: loadLocaleMessages(),
  silentTranslationWarn: true,
  allowComposition: true,
  allowObjects: true,
  // Keys are flat (see above), so a direct lookup replaces the default dotted-path resolver.
  messageResolver: (messages, path) => pluralObjectToPipe(messages?.[path]),
  pluralRules: {
    /**
     * @param choice {number} индекс выбора, переданный в $tc: `$tc('path.to.rule', choiceIndex)`
     * @param choicesLength {number} общее количество доступных вариантов
     * @returns финальный индекс для выбора соответственного варианта слова
     */
    ru(choice, choicesLength) {
      if (choice === 0) {
        return 0;
      }

      const teen = choice > 10 && choice < 20;
      const endsWithOne = choice % 10 === 1;

      if (choicesLength < 4) {
        return !teen && endsWithOne ? 1 : 2;
      }
      if (!teen && endsWithOne) {
        return 1;
      }
      if (!teen && choice % 10 >= 2 && choice % 10 <= 4) {
        return 2;
      }

      return choicesLength < 4 ? 2 : 3;
    },
  },
});

// Merge remote translations over the bundled floor. Fire-and-forget: the app
// boots on the bundled locales immediately and remote translations merge in
// reactively (via setLocaleMessage) once they resolve. Not awaited — top-level
// await isn't supported by the build target, and blocking boot is undesirable.
// Failures fall back to the bundled locales.
enhanceI18nWithRemote(instance, {
  apiUrl:
    globalThis?.import_meta_env?.VITE_APP_TRANSLATE_API_URL ||
    import.meta.env.VITE_APP_TRANSLATE_API_URL,
  apiKey:
    globalThis?.import_meta_env?.VITE_APP_TRANSLATE_API_KEY ||
    import.meta.env.VITE_APP_TRANSLATE_API_KEY,
  project: "tma",
  refreshIntervalMs:
    globalThis?.import_meta_env?.VITE_APP_TRANSLATE_UPDATE_INTERVAL_MS ||
    import.meta.env.VITE_APP_TRANSLATE_UPDATE_INTERVAL_MS,
  localMessages: loadLocaleMessages(),
}).catch((error) => {
  console.error(
    "i18n: remote translations enhance failed, using bundled locales",
    error,
  );
});

export default instance;
export const i18n = instance.global;
export const t = instance.global.t;
